import { useState, useEffect, useRef, useMemo } from 'react';
import useSWR from 'swr';
import { get } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import DesignPicker from '@/components/DesignPicker';

// Design Studio — rep-facing template customization editor.
// Pick a template (recipe) → the control panel renders an input for EVERY field
// the template actually exposes (team/mascot/sport/year/any text slot + a logo
// at the template's placement) + the ordered colorway. Live server-rendered
// preview via sport-templates /api/recipes/render-preview (proxied).

type Recipe = { id: string; name?: string; sport?: string; description?: string; svgMarkup?: string; slots?: string[] };
type School = { id: string; name?: string; mascot?: string; primary_color?: string; secondary_color?: string; city?: string; state?: string };
type SchoolColor = { id: string; name: string; hex: string };

// The 18 standardized GhostFit school colors are fetched from
// /api/school-colors (routes/proxy.js → supplier-catalog /api/catalog/colors →
// Firestore platformConfig/colorSystem). They used to be a hardcoded table right
// here, which is the drift the 2026-08 color unification removed from the
// services. Deliberately NO in-file fallback copy: if the palette can't be
// loaded, the color controls say so and go dead rather than offering the
// operator colors that may no longer be what we print.
const SLOT_LABELS = ['Primary', 'Secondary', 'Tertiary'];
const COLOR_ROLES = ['primary', 'secondary', 'tertiary'];

// A neutral sample logo so logo templates show their placement immediately.
const SAMPLE_LOGO = 'https://storage.googleapis.com/ghostfit-artwork-files/wizard-uploads/1777316233112-Ashwaubenon_Baseball.png';

// Per-slot label + default. Any slot not listed falls back to a humanized label.
const SLOT_META: Record<string, { label: string; def: (r?: Recipe) => string }> = {
  team_text: { label: 'Team text', def: () => 'CAMELOT' },
  mascot_text: { label: 'Mascot text', def: () => 'DRAGONS' },
  year: { label: 'Year / date', def: () => '2026' },
  sport: { label: 'Sport', def: (r) => (r?.sport ? r.sport.replace(/_/g, ' ').toUpperCase() : 'SPIRIT') },
};
const slotLabel = (slot: string) =>
  SLOT_META[slot]?.label || slot.replace(/_text$/, '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const slotDefault = (slot: string, r?: Recipe) =>
  SLOT_META[slot]?.def(r) ?? slot.replace(/_text$/, '').replace(/_/g, ' ').toUpperCase();

// Map an arbitrary hex (e.g. a Scout school color) to the nearest of the fetched
// palette tokens. Returns null when there is no hex to snap or no palette to snap
// onto — the caller keeps whatever the operator already had.
function nearestToken(hex: string, palette: SchoolColor[]): string | null {
  const h = String(hex || '').replace('#', '');
  if (h.length < 6 || palette.length === 0) return null;
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return null;
  let best = palette[0], bd = Infinity;
  for (const c of palette) {
    const ch = c.hex.replace('#', '');
    const dr = r - parseInt(ch.slice(0, 2), 16), dg = g - parseInt(ch.slice(2, 4), 16), db = b - parseInt(ch.slice(4, 6), 16);
    const d = dr * dr + dg * dg + db * db;
    if (d < bd) { bd = d; best = c; }
  }
  return best.id;
}

// Collect all values of a given attribute from an SVG string (string.match —
// no matchAll/iterator downleveling needed).
function attrValues(svg: string, attr: string): string[] {
  const all = svg.match(new RegExp(attr + '="[^"]+"', 'g')) || [];
  const prefix = attr.length + 2; // attr="
  return all.map((s) => s.slice(prefix, -1));
}

// The render response is SVG (image/svg+xml), not JSON, so we use a raw fetch
// rather than the JSON-parsing `post` helper. Mirrors api.ts's CSRF header.
async function renderPreview(payload: unknown): Promise<string> {
  const res = await fetch('/api/recipes/render-preview', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`render failed (${res.status})`);
  return res.text();
}

export default function DesignStudio() {
  const { data, isLoading } = useSWR<{ recipes?: Recipe[] } | Recipe[]>('/api/recipes', get);
  const recipes: Recipe[] = Array.isArray(data) ? data : (data?.recipes || []);

  // School palette — the only source is the server. No retry-forever loop and no
  // stale local copy: either we have the real table or the color panel is dead.
  const { data: colorResp, error: colorErr, isLoading: colorsLoading } =
    useSWR<{ colors?: SchoolColor[] }>('/api/school-colors', get);
  const schoolColors: SchoolColor[] = useMemo(
    () => (colorResp?.colors || []).filter((c) => c && c.id && c.hex),
    [colorResp],
  );
  const paletteReady = schoolColors.length > 0;
  const paletteError = colorErr
    ? 'Color palette unavailable — supplier-catalog is not answering.'
    : (!colorsLoading && !paletteReady ? 'Color palette came back empty.' : '');
  const hexOf = (token: string) => schoolColors.find((c) => c.id === token)?.hex || 'transparent';

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [vals, setVals] = useState<Record<string, string>>({ team_text: 'CAMELOT', mascot_text: 'DRAGONS' });
  const [logoUrl, setLogoUrl] = useState<string>(SAMPLE_LOGO);
  const [colorway, setColorway] = useState<string[]>(['royal', 'gold', 'white']);
  const [activeSlot, setActiveSlot] = useState(0);

  const [previewSvg, setPreviewSvg] = useState('');
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState('');
  const previewRef = useRef<HTMLDivElement>(null);

  // Selected recipe's full doc (svgMarkup + slots).
  const { data: detail } = useSWR<Recipe>(selectedId ? `/api/recipes/${selectedId}` : null, get);
  const svgMarkup = detail?.svgMarkup || '';

  // Every slot this template exposes — derived from the actual tagged markup,
  // unioned with the recipe's declared slots. This is what drives the panel.
  const slots = useMemo(() => {
    const set = new Set<string>();
    attrValues(svgMarkup, 'data-slot').forEach((s) => set.add(s));
    (detail?.slots || []).forEach((s) => set.add(s));
    return Array.from(set);
  }, [svgMarkup, detail?.slots]);
  const textSlots = slots.filter((s) => s !== 'logo_zone');
  const hasLogo = slots.includes('logo_zone');
  // Color roles the template uses (so we can show which colors matter).
  const usedColorRoles = useMemo(() => {
    const set = new Set<string>();
    attrValues(svgMarkup, 'data-color').forEach((r) => { if (COLOR_ROLES.includes(r)) set.add(r); });
    return set;
  }, [svgMarkup]);

  // Seed defaults for any newly-present slot when the template changes
  // (keeps values the rep has already typed for shared slots like team_text).
  const slotKey = textSlots.join('|');
  useEffect(() => {
    if (!textSlots.length) return;
    setVals((prev) => {
      const next = { ...prev };
      for (const s of textSlots) if (next[s] === undefined || next[s] === '') next[s] = slotDefault(s, detail);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotKey]);

  // School autofill.
  const [schoolQ, setSchoolQ] = useState('');
  const { data: schoolResp } = useSWR<{ schools?: School[] }>(
    schoolQ.trim().length >= 2 ? '/api/schools?limit=200' : null,
    get,
  );
  const schoolMatches = (schoolResp?.schools || [])
    .filter((s) => String(s.name || '').toLowerCase().includes(schoolQ.toLowerCase()))
    .slice(0, 6);

  function applySchool(s: School) {
    // Colors only move when we actually have a palette to snap onto; the text
    // fields still autofill either way.
    setColorway([
      (s.primary_color && nearestToken(s.primary_color, schoolColors)) || colorway[0],
      (s.secondary_color && nearestToken(s.secondary_color, schoolColors)) || colorway[1],
      colorway[2] || 'white',
    ]);
    setVals((prev) => ({
      ...prev,
      team_text: s.name ? s.name.toUpperCase() : prev.team_text,
      mascot_text: s.mascot ? s.mascot.toUpperCase() : prev.mascot_text,
    }));
    setSchoolQ('');
  }

  // Debounced server render whenever the template or any field changes.
  useEffect(() => {
    if (!svgMarkup) { setPreviewSvg(''); return; }
    const t = setTimeout(async () => {
      setRendering(true);
      setError('');
      try {
        const svg = await renderPreview({
          svgMarkup,
          schoolData: { colorway, ...vals },
          logoUrl: hasLogo ? logoUrl : '',
          format: 'svg',
        });
        setPreviewSvg(svg);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setRendering(false);
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svgMarkup, JSON.stringify(vals), colorway.join('|'), logoUrl, hasLogo]);

  // Inject the returned SVG via safe DOM methods (no innerHTML).
  useEffect(() => {
    const host = previewRef.current;
    if (!host) return;
    if (!previewSvg) { host.replaceChildren(); return; }
    try {
      const doc = new DOMParser().parseFromString(previewSvg, 'image/svg+xml');
      const el = doc.documentElement;
      el.removeAttribute('width');
      el.removeAttribute('height');
      el.setAttribute('style', 'width:100%;height:100%');
      host.replaceChildren(el);
    } catch {
      /* ignore parse errors */
    }
  }, [previewSvg]);

  function downloadSvg() {
    if (!previewSvg) return;
    const blob = new Blob([previewSvg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedId || 'design'}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="px-7 py-6">
      <h1 className="text-[20px] font-semibold tracking-tighter">Preview / Proof</h1>
      <p className="mb-4 text-[12px] text-muted">
        Step 2 (optional) · Pick a design — the panel shows every field that design uses (text, year, logo &amp; colors). Live preview; nothing is saved here.
      </p>

      <div className="grid items-start gap-5 lg:grid-cols-[320px_1fr_300px]">
        {/* Template picker — visual grid + sport dropdown */}
        <div className="rounded-md bg-surface p-3 shadow-card">
          {isLoading ? (
            <div className="space-y-2"><Skeleton className="h-8 w-full" /><Skeleton className="h-40 w-full" /></div>
          ) : recipes.length ? (
            <DesignPicker recipes={recipes} value={selectedId} onChange={(v) => setSelectedId(v as string)} cols={2} maxHeight={580} />
          ) : (
            <div className="p-4 text-center text-[11px] text-muted">
              No designs yet. Seed one with <code>POST /api/recipes</code>.
            </div>
          )}
        </div>

        {/* Live preview */}
        <div className="rounded-md border border-border bg-white p-4 shadow-card">
          <div className="flex aspect-square w-full items-center justify-center">
            {selectedId ? (
              <div ref={previewRef} className="flex h-full w-full items-center justify-center" />
            ) : (
              <div className="text-[12px] text-muted">Select a template to start.</div>
            )}
          </div>
          <div className="mt-2 h-4 text-[11px] text-muted">
            {rendering ? 'Rendering…' : error ? <span className="text-danger">{error}</span> : ''}
          </div>
        </div>

        {/* Controls — dynamic to the selected template's fields */}
        <div className="rounded-md bg-surface p-4 shadow-card">
          <label className="mb-1 block text-[11px] text-muted">School autofill</label>
          <Input value={schoolQ} onChange={(e) => setSchoolQ(e.target.value)} placeholder="Type a school name…" />
          {schoolMatches.length > 0 && (
            <div className="mt-1 overflow-hidden rounded-sm border border-border bg-bg">
              {schoolMatches.map((s) => (
                <button
                  key={s.id}
                  onClick={() => applySchool(s)}
                  className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-[11px] hover:bg-surface"
                >
                  <span className="flex shrink-0 gap-0.5">
                    {s.primary_color && <span className="h-3 w-3 rounded-full border border-border" style={{ background: s.primary_color }} />}
                    {s.secondary_color && <span className="h-3 w-3 rounded-full border border-border" style={{ background: s.secondary_color }} />}
                  </span>
                  <span className="truncate">{s.name}{s.mascot ? ` · ${s.mascot}` : ''}</span>
                </button>
              ))}
            </div>
          )}

          {/* What fields this template exposes */}
          {selectedId && (
            <div className="mt-4">
              <label className="mb-1 block text-[11px] text-muted">This design's fields</label>
              <div className="flex flex-wrap gap-1">
                {textSlots.map((s) => <Badge key={s} className="bg-bg text-text/80">{slotLabel(s)}</Badge>)}
                {hasLogo && <Badge className="bg-bg text-text/80">Logo</Badge>}
                {Array.from(usedColorRoles).map((r) => <Badge key={r} className="bg-bg text-text/60">{r} color</Badge>)}
                {!textSlots.length && !hasLogo && <span className="text-[11px] text-muted">Logo-only / no fillable fields.</span>}
              </div>
            </div>
          )}

          {/* One input per text slot the template actually uses */}
          {selectedId && textSlots.map((s) => (
            <div key={s}>
              <label className="mb-1 mt-3 block text-[11px] text-muted">{slotLabel(s)}</label>
              <Input value={vals[s] ?? ''} onChange={(e) => setVals((v) => ({ ...v, [s]: e.target.value }))} />
            </div>
          ))}

          {/* Logo (placed automatically at the template's logo position) */}
          {selectedId && hasLogo && (
            <div>
              <label className="mb-1 mt-3 block text-[11px] text-muted">Logo — placed at the template's position</label>
              <Input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="Logo image URL (PNG/SVG)" />
              <div className="mt-1 flex gap-2">
                <button onClick={() => setLogoUrl(SAMPLE_LOGO)} className="rounded-sm border border-border bg-white px-2 py-1 text-[11px] text-text/80 hover:bg-bg">Sample logo</button>
                <button onClick={() => setLogoUrl('')} className="rounded-sm border border-border bg-white px-2 py-1 text-[11px] text-text/80 hover:bg-bg">Clear</button>
              </div>
              <p className="mt-1 text-[10px] text-muted">Placement is set by the design — the preview shows exactly where it lands.</p>
            </div>
          )}

          {/* Colorway — slots used by this template are starred */}
          {selectedId && (
            <>
              <label className="mb-1 mt-4 block text-[11px] text-muted">Team colors — pick a slot, then a school color</label>
              {paletteError ? (
                <div className="rounded-sm border border-danger/40 bg-danger/5 p-2 text-[11px] text-danger">
                  {paletteError} Team colors can't be changed until it is back — the preview keeps the
                  colorway it already has.
                </div>
              ) : colorsLoading ? (
                <Skeleton className="h-24 w-full" />
              ) : (
                <>
                  <div className="flex gap-2">
                    {SLOT_LABELS.map((label, i) => {
                      const used = usedColorRoles.size === 0 || usedColorRoles.has(COLOR_ROLES[i]);
                      return (
                        <button
                          key={label}
                          onClick={() => setActiveSlot(i)}
                          className={`flex flex-1 items-center gap-1.5 rounded-sm border px-2 py-1.5 text-[11px] ${activeSlot === i ? 'border-primary ring-2 ring-primary/20' : 'border-border'} ${used ? '' : 'opacity-40'}`}
                          title={used ? '' : 'Not used by this design'}
                        >
                          <span className="h-4 w-4 shrink-0 rounded-full border border-border" style={{ background: hexOf(colorway[i]) }} />
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-2 grid grid-cols-6 gap-1.5">
                    {schoolColors.map((c) => (
                      <button
                        key={c.id}
                        title={c.name}
                        onClick={() => setColorway((cw) => cw.map((v, idx) => (idx === activeSlot ? c.id : v)))}
                        className="aspect-square rounded-sm border border-border"
                        style={{ background: c.hex, boxShadow: c.id === 'white' ? 'inset 0 0 0 1px #d0d3d4' : undefined }}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          <button
            onClick={downloadSvg}
            disabled={!previewSvg}
            className="mt-5 w-full rounded-sm border border-border bg-white px-3 py-2 text-[12px] text-text/85 hover:bg-bg disabled:opacity-50"
          >
            Download SVG (vector)
          </button>
        </div>
      </div>
    </div>
  );
}
